/**
 * `.onScrollVisibilityChange(threshold:_:)` (iOS 18; docs/ops-protocol.md,
 * "Scroll visibility"): a `styled` box with `scrollVisibility` is told when
 * the share of it inside its scroll view's visible area crosses its
 * threshold. The visible area is the scroll view's box less a bar it scrolls
 * under (its `topInset`) and a bar covering its bottom (`bottomInset`), as
 * content under a bar is not in view; outside a scroll view it is the root.
 */

export interface Span {
  start: number;
  length: number;
}

/** The share (0...1) of `target` along an axis inside `viewport`; 0 for a target of no extent. */
export function visibleShare(target: Span, viewport: Span): number {
  if (target.length <= 0) return 0;
  const start = Math.max(target.start, viewport.start);
  const end = Math.min(target.start + target.length, viewport.start + viewport.length);
  return Math.max(0, end - start) / target.length;
}

/**
 * Whether a box is visible: some of it is in view, and at least `threshold`
 * of it along the scroll axes (both shares multiplied for a view scrolling
 * both ways, the other axis counted too, as a box off to the side is not in
 * view).
 */
export function isScrollVisible(
  target: { x: number; y: number; width: number; height: number },
  viewport: { x: number; y: number; width: number; height: number },
  threshold: number,
): boolean {
  const horizontal = visibleShare({ start: target.x, length: target.width }, { start: viewport.x, length: viewport.width });
  const vertical = visibleShare({ start: target.y, length: target.height }, { start: viewport.y, length: viewport.height });
  const share = horizontal * vertical;
  return share > 0 && share >= Math.min(1, Math.max(0, threshold));
}
